interface CountProps {
  value: number
  mode?: CountMode
  ariaLabel?: string
  className?: string
}

export const CountMode = {
  Exact: 0,
  Approximate: 1,
  LowerBound: 2,
} as const

export type CountMode = (typeof CountMode)[keyof typeof CountMode]

// Primer Pattern Request: https://github.com/github/primer/issues/956
export function Count({value, mode = CountMode.Exact, ariaLabel, className}: CountProps) {
  if (!Number.isFinite(value)) return null

  return (
    // eslint-disable-next-line github/a11y-role-supports-aria-props
    <span aria-label={ariaLabel} className={className}>
      {getValueText(value)}
      {mode === CountMode.LowerBound ? <span>{'+ '}</span> : null}
    </span>
  )
}

export function getCountText(value: number, mode: CountMode = CountMode.Exact) {
  return getValueText(value) + (mode === CountMode.LowerBound ? '+' : '')
}

export function getValueText(value: number, max?: number): string {
  if (max !== undefined && value >= max) {
    return `${getValueText(max)}+`
  }

  if (value < 0) {
    return `-${getValueText(Math.abs(value))}`
  }

  if (value >= 1_000_000_000) {
    return `${roundToOneDecimalPlace(value / 1_000_000_000)}B`
  }

  if (value >= 100_000_000) {
    return `${Math.round(value / 1_000_000)}M`
  }

  if (value >= 1_000_000) {
    return `${roundToOneDecimalPlace(value / 1_000_000)}M`
  }

  if (value >= 100_000) {
    return `${Math.round(value / 1_000)}k`
  }

  if (value >= 1_000) {
    return `${roundToOneDecimalPlace(value / 1_000)}k`
  }

  return `${Math.round(value)}`
}

function roundToOneDecimalPlace(value: number) {
  return Math.round(value * 10) / 10
}
